/**
 * MoAHistoryTab — Brief 51.1 (14 Jul 2026).
 *
 * Table view of a matter's full Money-on-Account movement ledger
 * (receipts, drawdowns, refunds, transfers) newest first. Mounted as
 * a section on the client / matter detail page. See MatterIntakeDetail.jsx
 * for the mount site.
 *
 * Columns (spec-locked):
 *   Date | Type | Amount | Balance | Requested by | Recorded by |
 *   Counterparty | Ref | Linked PR
 *
 * States: loading skeleton, error band + retry, empty message,
 * populated table. Conventions mirror ``PaymentRequestsWidget`` so
 * the two widgets read as a coherent pair.
 *
 * Legacy rows without ``requested_by_initials`` render an em-dash
 * ("—") in that column, not a blank cell — the FE contract is
 * "no field on the row" ≠ "field with empty value".
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { RefreshCw, AlertCircle, Wallet, ExternalLink } from "lucide-react";
import { API } from "../config/api";
import { getUserRole, ROLES } from "../config/roleConfig";
import ReverseControl from "./ReverseLedgerEntry";

const MOVEMENT_LABEL = {
  receipt:  { label: "Receipt",  color: "bg-teal/15 text-teal border-teal/30" },
  drawdown: { label: "Drawdown", color: "bg-status-amber-dim/20 text-status-amber border-status-amber/30" },
  refund:   { label: "Refund",   color: "bg-status-red-dim/20 text-status-red border-status-red/30" },
  transfer: { label: "Transfer", color: "bg-white/10 text-slate-300 border-white/20" },
};

const OPENING_LABEL = { label: "Opening", color: "bg-indigo-500/15 text-indigo-200 border-indigo-400/30" };

const EM_DASH = "\u2014";

// BM-H4699: corrections are made here by Accounts and the directors, the
// same people the reverse route lets through.
function _mayCorrect() {
  const email = sessionStorage.getItem("bonsai_user_email") || localStorage.getItem("bonsai_user_email") || "";
  return [ROLES.DIRECTOR, ROLES.ACCOUNTS].includes(getUserRole(email));
}

function _fmtDate(iso) {
  if (!iso) return EM_DASH;
  const d = new Date(iso);
  if (isNaN(d)) return EM_DASH;
  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleString("en-GB", { month: "short" });
  return `${day} ${month} ${d.getFullYear()}`;
}

export function fmtSignedGbp(raw) {
  if (raw === null || raw === undefined || raw === "") return EM_DASH;
  const n = Number(raw);
  if (!Number.isFinite(n)) return EM_DASH;
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  return `${sign}\u00a3${abs.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function _headers() {
  const email =
    sessionStorage.getItem("bonsai_user_email") ||
    localStorage.getItem("bonsai_user_email") || "";
  return {
    "X-Bonsai-User-Id":
      sessionStorage.getItem("bonsai_user_id") ||
      localStorage.getItem("bonsai_user_id") || email,
    "X-User-Email": email,
  };
}

export default function MoAHistoryTab({ matterReference }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const mayCorrect = _mayCorrect();

  const load = useCallback(async () => {
    if (!matterReference) {
      setLoading(false);
      return;
    }
    setError("");
    try {
      const res = await axios.get(
        `${API}/money-on-account/matters/${encodeURIComponent(matterReference)}/moa-history`,
        { headers: _headers() },
      );
      setRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
    } catch {
      setError("Couldn't load MoA history");
    } finally {
      setLoading(false);
    }
  }, [matterReference]);

  useEffect(() => { load(); }, [load]);

  return (
    <section
      className="mt-8 bg-navy-card border border-white/10 rounded-xl p-4"
      data-testid="moa-history-tab"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-white flex items-center gap-2">
          <Wallet className="w-4 h-4 text-teal" />
          MoA History
        </h3>
        <button
          onClick={() => { setLoading(true); load(); }}
          className="text-gray-400 hover:text-white"
          aria-label="Refresh"
          data-testid="moa-history-tab-refresh"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {loading ? (
        <div className="space-y-1.5" data-testid="moa-history-tab-loading">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-8 bg-white/5 rounded animate-pulse"
            />
          ))}
        </div>
      ) : error ? (
        <div
          className="flex items-center gap-2 bg-status-red-dim/20 border border-status-red/30 rounded-lg px-3 py-2"
          data-testid="moa-history-tab-error"
        >
          <AlertCircle className="w-4 h-4 text-status-red shrink-0" />
          <span className="text-xs text-status-red flex-1">{error}</span>
          <button
            onClick={() => { setLoading(true); load(); }}
            className="text-status-red hover:text-white shrink-0"
            aria-label="Retry"
            data-testid="moa-history-tab-retry"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : rows.length === 0 ? (
        <p
          className="text-xs text-gray-500 py-6 text-center"
          data-testid="moa-history-tab-empty"
        >
          No Money on Account movements yet
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table
            className="w-full text-xs"
            data-testid="moa-history-tab-table"
          >
            <thead>
              <tr className="text-slate-500 uppercase tracking-wider text-[10px]">
                <th className="text-left font-semibold px-2 py-1.5">Date</th>
                <th className="text-left font-semibold px-2 py-1.5">Type</th>
                <th className="text-right font-semibold px-2 py-1.5">Amount</th>
                <th className="text-right font-semibold px-2 py-1.5">Balance</th>
                <th className="text-left font-semibold px-2 py-1.5">Requested by</th>
                <th className="text-left font-semibold px-2 py-1.5">Recorded by</th>
                <th className="text-left font-semibold px-2 py-1.5">Counterparty</th>
                <th className="text-left font-semibold px-2 py-1.5">Ref</th>
                <th className="text-left font-semibold px-2 py-1.5">Linked PR</th>
                <th className="px-2 py-1.5" />
              </tr>
            </thead>
            <tbody className="text-slate-300">
              {rows.map((r) => {
                const badge = (r.source === "opening_balance" && OPENING_LABEL)
                  || MOVEMENT_LABEL[r.movement_type]
                  || { label: r.movement_type || "?", color: "bg-white/5 text-slate-400 border-white/10" };
                const amountNeg = Number(r.amount_gbp) < 0;
                return (
                  <tr
                    key={r._id}
                    className={`border-t border-white/5 ${r.reversed_by_entry_id ? "opacity-50" : ""}`}
                    data-testid={`moa-history-tab-row-${r._id}`}
                  >
                    <td className="px-2 py-1.5 whitespace-nowrap">{_fmtDate(r.occurred_at)}</td>
                    <td className="px-2 py-1.5">
                      <span className={`inline-block px-1.5 py-0.5 rounded-full border text-[10px] ${badge.color}`}>
                        {badge.label}
                      </span>
                    </td>
                    <td
                      className={`px-2 py-1.5 text-right tabular-nums ${amountNeg ? "text-status-red" : "text-white"}`}
                      data-testid={`moa-history-tab-amount-${r._id}`}
                    >
                      {fmtSignedGbp(r.amount_gbp)}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-slate-400">
                      {fmtSignedGbp(r.running_balance_gbp)}
                    </td>
                    <td
                      className="px-2 py-1.5 font-mono"
                      data-testid={`moa-history-tab-requested-by-${r._id}`}
                    >
                      {r.requested_by_initials || EM_DASH}
                    </td>
                    <td className="px-2 py-1.5 font-mono">{r.recorded_by_initials || EM_DASH}</td>
                    <td className="px-2 py-1.5 truncate max-w-[140px]">{r.counterparty || EM_DASH}</td>
                    <td className="px-2 py-1.5 truncate max-w-[100px] font-mono">{r.reference || EM_DASH}</td>
                    <td className="px-2 py-1.5">
                      {r.linked_payment_request_id ? (
                        <Link
                          to={`/payment-request/${r.linked_payment_request_id}`}
                          className="text-teal hover:underline inline-flex items-center gap-0.5"
                          data-testid={`moa-history-tab-linked-pr-${r._id}`}
                        >
                          PR
                          <ExternalLink className="w-2.5 h-2.5" />
                        </Link>
                      ) : (
                        EM_DASH
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">
                      {r.reverses_entry_id && <span className="text-[10px] text-slate-500">correction</span>}
                      {r.reversed_by_entry_id && <span className="text-[10px] text-slate-500">reversed</span>}
                      {mayCorrect && !r.reverses_entry_id && !r.reversed_by_entry_id && (
                        <ReverseControl entryId={r._id} onDone={load} testPrefix="moa-history-reverse" />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
